import React, { useEffect, useState } from 'react';

import * as Sentry from '@sentry/nextjs';

import Button from 'epn-ui/umd/dist/components/Button';
import Dialog from 'epn-ui/umd/dist/components/Dialog';
import notification from 'epn-ui/umd/dist/components/Notification';
import Select from 'epn-ui/umd/dist/components/Select';

import api from '@api';
import { IGetPlacementsDataFormatted } from '@api/types';
import BaseEmpty from '@base/BaseEmpty';
import useI18n from '@i18n';

export interface IUniqPlacementOption {
    label: string;
    value: string;
    info: IGetPlacementsDataFormatted;
}

interface IConnectionModal {
    isOpen: boolean;
    onClose: () => void;
    offerName: string;
    offerId: number;
    placements: IUniqPlacementOption[];
    onConnectionSuccess: () => void;
    currentPlacementValue: string;
}

const connectOfferToPlacement = async (params: {
    title: string;
    url: string;
    categoryId: number;
    offerId: number;
}): Promise<void> => {
    await api.placement.addUserPlacement({
        body: params,
    });
};

const ConnectionModal: React.FC<IConnectionModal> = ({
    isOpen,
    onClose,
    offerName,
    offerId,
    placements,
    onConnectionSuccess,
    currentPlacementValue,
}) => {
    const { t, locale } = useI18n('Offers');
    const { t: tErrors } = useI18n('Errors');
    const [currentPlacementOption, setCurrentPlacementOption] = useState<IUniqPlacementOption>(
        null,
    );
    const [isConnectionLoading, setIsConnectionLoading] = useState(false);

    const setCurrentPlacement = value => {
        const currentOption = placements.find(placement => placement.value === value);
        setCurrentPlacementOption(currentOption);
    };

    useEffect(() => {
        if (currentPlacementValue && !currentPlacementOption?.value) {
            setCurrentPlacement(currentPlacementValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPlacementValue]);

    const onConnect = async () => {
        const { title, url, trafficSourceId } = currentPlacementOption.info;
        const params = { title, url, categoryId: +trafficSourceId, offerId };

        setIsConnectionLoading(true);

        try {
            await connectOfferToPlacement(params);
            onConnectionSuccess();
            setIsConnectionLoading(false);
        } catch (error) {
            const { errors: addDomainErrors = [] } = (await error.response?.json()) || {};
            const { error: errorCode } = addDomainErrors[0] || {};

            if (errorCode === 422001) {
                notification.error({
                    message: t('The placement was previously connected to the offer'),
                });
            }

            if (errorCode !== 422001) {
                Sentry.captureException(error);
                notification.error({ message: tErrors('Something went wrong') });
            }
            setIsConnectionLoading(false);
        }
    };

    return (
        <Dialog onCancel={onClose} open={isOpen} size="sm">
            <div className="p-[8px] xs-[16px]">
                <p className="font-bold text-center text-blueGray-800 text-[24px] leading-[35px]">
                    {t('Connecting to the offer')}
                </p>
                {offerName !== null && (
                    <p className="w-full text-center text-blueGray-600 mt-[8px]">{offerName}</p>
                )}
                <Select
                    id="OffersOrderSelect"
                    className="w-full mt-[24px]"
                    value={currentPlacementOption?.value}
                    onChange={value => setCurrentPlacement(value)}
                    options={placements}
                    placeholder={t('Select placement')}
                    notFoundContent={<BaseEmpty />}
                />
                {currentPlacementOption !== null && currentPlacementOption?.info?.title && (
                    <p className="flex flex-row bg-blueGray-100 rounded-[4px] py-[5px] px-[12px] gap-[16px] mt-[16px]">
                        <span className="font-medium text-blueGray-800">{t('Link')}</span>
                        <span className="text-blueGray-600">
                            {currentPlacementOption?.info?.url}
                        </span>
                    </p>
                )}
                <p />
                <div className="flex flex-col w-full gap-[16px] mt-[24px]">
                    <Button
                        id={`CouponsPageCouponGetLinkButton${offerName}`}
                        type="primary"
                        disabled={currentPlacementOption === null}
                        className="w-full"
                        loading={isConnectionLoading}
                        onClick={onConnect}
                    >
                        {t('OfferConnectModalConnect')}
                    </Button>
                    <Button
                        id={`CouponsPageCouponGetLinkButton${offerName}`}
                        type="default"
                        href={`/${locale}/placements/add?offerId=${offerId}`}
                        disabled={isConnectionLoading}
                        className="w-full"
                        target="_blank"
                    >
                        {t('Add a new placement')}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

export default ConnectionModal;
